/**
 * Générateur — Identifier une instruction ou une séquence d'instructions
 * Attendu ID : 687 | Niveau : 6ème | Domaine : Algorithmique (initiation à la programmation)
 *
 * Types générés (1 catégorie, 5 formats) :
 *   - programme_vers_chemin_qcm : QCM 3 choix — quel chemin correspond au programme ?
 *   - chemin_vers_programme_qcm : QCM 4 choix — quelle séquence produit ce chemin ?
 *   - instruction_fautive_qcm   : QCM 4 choix — quelle instruction faut-il corriger ?
 *   - programmes_chemins_relier : relier 3 programmes à leurs chemins (+ 1 chemin leurre)
 *   - lecture_programme_vf      : V/F 4 lignes — lecture d'une séquence affichée
 *
 * Langage (eduscol cycle 3, débranché) : déplacements ABSOLUS sur quadrillage
 * 5×5, une instruction = une case, flèches ↑ ↓ ← →. Le robot part de la case
 * marquée « D ». Frontière d'attendu : on IDENTIFIE des séquences données —
 * jamais d'écriture de programme (688), de boucle (689), ni de construction
 * de chemin (690).
 *
 * Invariants par construction :
 *   • chemins auto-évitants, toujours dans la grille (tirage avec retentatives) ;
 *   • leurres par mutation vérifiée (inversion, échange, substitution) : jamais
 *     égaux au chemin correct, jamais égaux entre eux, mutation en position ≥ 2
 *     pour que la première instruction ne trahisse rien ;
 *   • vignettes comparées : même grille, même départ (aucun indice parasite) ;
 *   • V/F : clés sémantiques distinctes, quota variable.
 *
 * Sources d'inspiration (recherche 2026-08-11) : Sésamath livre d'exercices
 * d'algorithmique (ex. 1, 2, 8), eduscol « Initiation à la programmation »
 * (appliquer / corriger un codage), carnet d'algorithmique débranchée cycle 3.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeRelier,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 687;

const L_PVC = 'Retrouver le chemin d\'un programme (QCM)';
const L_CVP = 'Retrouver le programme d\'un chemin (QCM)';
const L_FAUT = 'Corriger l\'instruction fautive (QCM)';
const L_REL = 'Associer programmes et chemins (relier)';
const L_VF  = 'Lire une séquence d\'instructions (V/F)';

const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Moteur de déplacements ──────────────────────────────────

const TAILLE = 5; // grille 5×5

const DIRS = {
    '↑': { dx: 0, dy: -1, nom: 'monter d\'une case' },
    '↓': { dx: 0, dy: 1, nom: 'descendre d\'une case' },
    '←': { dx: -1, dy: 0, nom: 'aller d\'une case vers la gauche' },
    '→': { dx: 1, dy: 0, nom: 'aller d\'une case vers la droite' },
};
const FLECHES = Object.keys(DIRS);
const OPPOSE = { '↑': '↓', '↓': '↑', '←': '→', '→': '←' };

/** Exécute un programme depuis un départ. Retourne les cases visitées, ou null si sortie de grille. */
const executer = (depart, prog) => {
    const cells = [{ ...depart }];
    let { x, y } = depart;
    for (const f of prog) {
        x += DIRS[f].dx;
        y += DIRS[f].dy;
        if (x < 0 || y < 0 || x >= TAILLE || y >= TAILLE) return null;
        cells.push({ x, y });
    }
    return cells;
};

const cleChemin = (cells) => cells.map(c => `${c.x},${c.y}`).join('|');

/** Chemin auto-évitant (dessin lisible) : aucune case visitée deux fois. */
const estAutoEvitant = (cells) =>
    cells !== null && new Set(cells.map(c => `${c.x},${c.y}`)).size === cells.length;

/** Tire un programme auto-évitant de `longueur` instructions et son départ. */
const tirerChemin = (longueur) => {
    for (let essai = 0; essai < 200; essai++) {
        const depart = { x: randInt(0, TAILLE - 1), y: randInt(0, TAILLE - 1) };
        const prog = [];
        for (let i = 0; i < longueur; i++) {
            const interdites = i > 0 ? [OPPOSE[prog[i - 1]]] : [];
            prog.push(randomChoice(FLECHES.filter(f => !interdites.includes(f))));
        }
        const cells = executer(depart, prog);
        if (estAutoEvitant(cells)) return { depart, prog, cells };
    }
    // Repli déterministe (jamais atteint en pratique) : escalier depuis (0;4)
    const depart = { x: 0, y: TAILLE - 1 };
    const prog = ['→', '↑', '→', '↑'].slice(0, longueur);
    return { depart, prog, cells: executer(depart, prog) };
};

// ─── Mutations (leurres vérifiés) ────────────────────────────
// Position mutée ≥ 1 : la 1re instruction ne départage jamais à elle seule.

const mutations = [
    // inversion : une instruction devient son opposée
    (prog) => {
        const i = randInt(1, prog.length - 1);
        const copie = [...prog];
        copie[i] = OPPOSE[copie[i]];
        return copie;
    },
    // échange : deux instructions voisines différentes sont permutées
    (prog) => {
        const candidats = [];
        for (let i = 1; i < prog.length - 1; i++) {
            if (prog[i] !== prog[i + 1]) candidats.push(i);
        }
        if (!candidats.length) return null;
        const i = randomChoice(candidats);
        const copie = [...prog];
        [copie[i], copie[i + 1]] = [copie[i + 1], copie[i]];
        return copie;
    },
    // substitution : une instruction devient perpendiculaire
    (prog) => {
        const i = randInt(1, prog.length - 1);
        const copie = [...prog];
        const perp = DIRS[copie[i]].dx === 0 ? ['←', '→'] : ['↑', '↓'];
        copie[i] = randomChoice(perp);
        return copie;
    },
];

/**
 * Produit `nb` programmes leurres depuis (depart, prog) : chemins valides si
 * `dessinable`, tous distincts entre eux et du programme correct (comparaison
 * sur les cases visitées ET sur la séquence).
 */
const tirerLeurres = (depart, prog, nb, dessinable) => {
    const cleBonne = cleChemin(executer(depart, prog));
    const vus = new Set([cleBonne, prog.join('')]);
    const leurres = [];
    for (let essai = 0; essai < 400 && leurres.length < nb; essai++) {
        const candidat = randomChoice(mutations)(prog);
        if (!candidat) continue;
        const cells = executer(depart, candidat);
        if (dessinable && !estAutoEvitant(cells)) continue;
        const cle = cells ? cleChemin(cells) : `hors:${candidat.join('')}`;
        if (vus.has(cle) || vus.has(candidat.join(''))) continue;
        vus.add(cle);
        vus.add(candidat.join(''));
        leurres.push({ prog: candidat, cells });
    }
    return leurres.length === nb ? leurres : null;
};

// ─── Rendu SVG ───────────────────────────────────────────────

const CASE = 26;
const BLEU = '#1E3A8A';
const VERT = '#1D9E75';

/** Groupe SVG : une grille 5×5 avec départ « D » et chemin tracé. */
const svgGrille = (ox, oy, cells, label) => {
    const w = TAILLE * CASE;
    const lignes = [];
    for (let i = 0; i <= TAILLE; i++) {
        lignes.push(`<line x1="${ox + i * CASE}" y1="${oy}" x2="${ox + i * CASE}" y2="${oy + w}" stroke="#cbd5e1" stroke-width="1"/>`);
        lignes.push(`<line x1="${ox}" y1="${oy + i * CASE}" x2="${ox + w}" y2="${oy + i * CASE}" stroke="#cbd5e1" stroke-width="1"/>`);
    }
    const centre = (c) => ({ cx: ox + c.x * CASE + CASE / 2, cy: oy + c.y * CASE + CASE / 2 });
    const pts = cells.map(c => { const p = centre(c); return `${p.cx},${p.cy}`; }).join(' ');
    const dep = centre(cells[0]);
    const fin = centre(cells[cells.length - 1]);
    const avant = centre(cells[cells.length - 2]);
    // pointe de flèche orientée selon le dernier déplacement
    const vx = Math.sign(fin.cx - avant.cx), vy = Math.sign(fin.cy - avant.cy);
    const pointe = `<path d="M${fin.cx + vx * 6},${fin.cy + vy * 6} L${fin.cx - vy * 4 - vx * 2},${fin.cy - vx * 4 - vy * 2} L${fin.cx + vy * 4 - vx * 2},${fin.cy + vx * 4 - vy * 2} Z" fill="${VERT}"/>`;
    const titre = label
        ? `<text x="${ox + w / 2}" y="${oy - 8}" font-size="12" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${label}</text>`
        : '';
    return `${titre}<rect x="${ox}" y="${oy}" width="${w}" height="${w}" fill="none" stroke="${BLEU}" stroke-width="1.4"/>${lignes.join('')}` +
        `<polyline points="${pts}" fill="none" stroke="${VERT}" stroke-width="2.4"/>${pointe}` +
        `<circle cx="${dep.cx}" cy="${dep.cy}" r="8" fill="${BLEU}"/>` +
        `<text x="${dep.cx}" y="${dep.cy + 3.5}" font-size="10" font-weight="700" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">D</text>`;
};

/** Groupe SVG : un programme en blocs-flèches numérotés. */
const svgProgramme = (ox, oy, prog, numerote) => {
    const blocs = prog.map((f, i) => {
        const x = ox + i * 30;
        const num = numerote
            ? `<text x="${x + 11}" y="${oy + 36}" font-size="9" fill="#64748b" text-anchor="middle" font-family="system-ui,sans-serif">${i + 1}</text>`
            : '';
        return `<rect x="${x}" y="${oy}" width="22" height="22" rx="4" fill="#EFF6FF" stroke="${BLEU}" stroke-width="1.2"/>` +
            `<text x="${x + 11}" y="${oy + 16}" font-size="14" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${f}</text>${num}`;
    });
    return blocs.join('');
};

const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

const enteteSvg = (w, h, aria) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="${aria}">`;

const progEnTexte = (prog) => prog.join(' ');

// Signature d'anti-répétition (revue 2026-08-12) : le texte de ces formats
// est fixe, tout le contenu variable vit dans le SVG — signer sur le texte
// seul ne dédupliquait rien (et retirait 8 fois pour rien à chaque tirage).
const signatureDe = (payload) =>
    (payload.elements_additionnels?.elements?.[0]?.config?.imageUrl || '') + payload.texte;

// ─── Format 1 — Programme → chemin (QCM) ─────────────────────

let derniereSignaturePVC = null;

const genProgrammeVersCheminQcm = () => {
    let payload = construireProgrammeVersCheminQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignaturePVC; t++) {
        payload = construireProgrammeVersCheminQcm();
    }
    derniereSignaturePVC = signatureDe(payload);
    return payload;
};

const construireProgrammeVersCheminQcm = () => {
    let tirage, leurres;
    do {
        tirage = tirerChemin(randInt(4, 6));
        leurres = tirerLeurres(tirage.depart, tirage.prog, 2, true);
    } while (leurres === null);

    const chemins = shuffle([
        { cells: tirage.cells, correct: true },
        { cells: leurres[0].cells, correct: false },
        { cells: leurres[1].cells, correct: false },
    ]);
    const lettres = ['A', 'B', 'C'];
    const largeurGrille = TAILLE * CASE;
    const progLargeur = tirage.prog.length * 30 - 8;
    const svg = enteteSvg(3 * largeurGrille + 60, largeurGrille + 90,
        `Un programme de ${tirage.prog.length} flèches, puis trois grilles nommées chemin A, B et C montrant chacune un trajet depuis la case départ`)
        + svgProgramme((3 * largeurGrille + 60 - progLargeur) / 2, 12, tirage.prog, false)
        + chemins.map((c, i) => svgGrille(10 + i * (largeurGrille + 20), 70, c.cells, `Chemin ${lettres[i]}`)).join('')
        + '</svg>';

    const bonne = `Chemin ${lettres[chemins.findIndex(c => c.correct)]}`;
    const texte = `<p>Le robot part de la case marquée « D » et exécute le programme ci-dessus. Chaque flèche le déplace d'une case. Quel chemin correspond à ce programme ?</p>`;

    const payload = makeQcm(lettres.map(l => `Chemin ${l}`), bonne, texte, opts(L_PVC));
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'programme_vers_chemin_qcm';
    return payload;
};

// ─── Format 2 — Chemin → programme (QCM) ─────────────────────

let derniereSignatureCVP = null;

const genCheminVersProgrammeQcm = () => {
    let payload = construireCheminVersProgrammeQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureCVP; t++) {
        payload = construireCheminVersProgrammeQcm();
    }
    derniereSignatureCVP = signatureDe(payload);
    return payload;
};

const construireCheminVersProgrammeQcm = () => {
    let tirage, leurres;
    do {
        tirage = tirerChemin(randInt(4, 6));
        leurres = tirerLeurres(tirage.depart, tirage.prog, 3, false);
    } while (leurres === null);

    const largeurGrille = TAILLE * CASE;
    const svg = enteteSvg(largeurGrille + 20, largeurGrille + 20,
        'Une grille avec la case départ D et un chemin tracé, terminé par une pointe de flèche')
        + svgGrille(10, 10, tirage.cells, '')
        + '</svg>';

    const bonne = progEnTexte(tirage.prog);
    const choix = [bonne, ...leurres.map(l => progEnTexte(l.prog))];
    const texte = `<p>Le robot part de la case marquée « D » et suit le chemin tracé ci-dessus. Chaque flèche le déplace d'une case. Quelle séquence d'instructions produit exactement ce chemin ?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_CVP));
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'chemin_vers_programme_qcm';
    return payload;
};

// ─── Format 3 — Instruction fautive (QCM) ────────────────────

let derniereSignatureFaut = null;

const genInstructionFautiveQcm = () => {
    let payload = construireInstructionFautiveQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureFaut; t++) {
        payload = construireInstructionFautiveQcm();
    }
    derniereSignatureFaut = signatureDe(payload);
    return payload;
};

const construireInstructionFautiveQcm = () => {
    const tirage = tirerChemin(randInt(4, 5));
    const { prog } = tirage;
    // Une instruction est remplacée par une autre flèche — la 1re position
    // est autorisée (revue 2026-08-12 : l'interdire faisait de « n°1 » un
    // choix jamais correct, éliminable gratuitement).
    const posFaute = randInt(0, prog.length - 1);
    const fautif = [...prog];
    fautif[posFaute] = randomChoice(FLECHES.filter(f => f !== prog[posFaute]));

    const largeurGrille = TAILLE * CASE;
    const progLargeur = fautif.length * 30 - 8;
    const w = Math.max(largeurGrille + 20, progLargeur + 20);
    const svg = enteteSvg(w, largeurGrille + 80,
        'Une grille avec le chemin attendu depuis la case départ D, et en dessous un programme numéroté dont une instruction est fausse')
        + svgGrille((w - largeurGrille) / 2, 10, tirage.cells, '')
        + svgProgramme((w - progLargeur) / 2, largeurGrille + 26, fautif, true)
        + '</svg>';

    const bonne = `L'instruction n°${posFaute + 1}`;
    // 4 positions proposées, dont la fautive
    const autres = shuffle([...Array(prog.length).keys()].filter(i => i !== posFaute)).slice(0, 3);
    const choix = [posFaute, ...autres].sort((a, b) => a - b).map(i => `L'instruction n°${i + 1}`);
    const texte = `<p>Le chemin tracé ci-dessus est celui que le robot doit suivre depuis la case « D ». Le programme numéroté en dessous devrait produire ce chemin, mais <strong>une seule instruction est fausse</strong>. Quelle instruction faut-il corriger ?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_FAUT));
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'instruction_fautive_qcm';
    return payload;
};

// ─── Format 4 — Programmes ↔ chemins (relier) ────────────────

let derniereSignatureRel = null;

const genProgrammesCheminReliers = () => {
    let payload = construireProgrammesCheminReliers();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureRel; t++) {
        payload = construireProgrammesCheminReliers();
    }
    derniereSignatureRel = signatureDe(payload);
    return payload;
};

const construireProgrammesCheminReliers = () => {
    // 4 chemins (3 à relier + 1 leurre) à programmes tous distincts.
    const tirages = [];
    const clesProg = new Set();
    const clesChemin = new Set();
    for (let essai = 0; essai < 200 && tirages.length < 4; essai++) {
        const t = tirerChemin(randInt(3, 4));
        const cp = t.prog.join('');
        const cc = cleChemin(t.cells);
        if (clesProg.has(cp) || clesChemin.has(cc)) continue;
        clesProg.add(cp);
        clesChemin.add(cc);
        tirages.push(t);
    }
    if (tirages.length < 4) return construireProgrammesCheminReliers();

    const lettres = ['A', 'B', 'C', 'D'];
    const largeurGrille = TAILLE * CASE;
    const svg = enteteSvg(4 * largeurGrille + 90, largeurGrille + 40,
        'Quatre grilles nommées chemin A, B, C et D, chacune avec sa case départ D et un trajet tracé')
        + tirages.map((t, i) => svgGrille(10 + i * (largeurGrille + 20), 30, t.cells, `Chemin ${lettres[i]}`)).join('')
        + '</svg>';

    // 3 programmes à relier, le 4e chemin reste sans programme (leurre).
    const indices = shuffle([0, 1, 2, 3]);
    const aRelier = indices.slice(0, 3);
    const indiceLeurre = indices[3];
    const pairs = aRelier.map(i => ({ source: progEnTexte(tirages[i].prog), target: `Chemin ${lettres[i]}` }));
    const leurres = [{ target: `Chemin ${lettres[indiceLeurre]}`, code: 'chemin_sans_programme' }];

    const texte = `<p>Le robot part toujours de la case marquée « D » ; chaque flèche le déplace d'une case. Relie chaque programme au chemin qu'il produit. Attention : un des chemins ne correspond à aucun programme.</p>`;

    const payload = makeRelier(pairs, texte, { ...opts(L_REL), cibles_leurres: leurres });
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'programmes_chemins_relier';
    return payload;
};

// ─── Format 5 — Lecture d'une séquence (V/F) ─────────────────

let derniereSignatureVF = null;

const genLectureProgrammeVf = () => {
    let payload = construireLectureProgrammeVf();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureVF; t++) {
        payload = construireLectureProgrammeVf();
    }
    derniereSignatureVF = signatureDe(payload);
    return payload;
};

// Chaque builder retourne { l (ligne), v (true si vraie), pos (positions du
// programme visées, base 1) } — les positions doivent être deux à deux
// disjointes dans un tirage (revue 2026-08-12 : « la première instruction »
// et « l'instruction n°1 » sortaient ensemble dans 49 % des tirages).
const BUILDERS_VF_687 = [
    {
        k: 'nb_total',
        b: (prog, vrai) => {
            const n = vrai ? prog.length : prog.length + randomChoice([-1, 1]);
            return { l: `Ce programme contient ${n} instructions.`, v: vrai, pos: [] };
        },
    },
    {
        k: 'nature_k',
        // Positions intérieures uniquement : n°1 et n°longueur appartiennent
        // aux builders « première » / « dernière ».
        b: (prog, vrai) => {
            const i = randInt(1, prog.length - 2);
            const dir = vrai ? prog[i] : randomChoice(FLECHES.filter(f => f !== prog[i]));
            return { l: `L'instruction n°${i + 1} fait ${DIRS[dir].nom}.`, v: vrai, pos: [i + 1] };
        },
    },
    {
        k: 'identiques',
        b: (prog, vrai) => {
            const paires = [];
            for (let i = 0; i < prog.length; i++) {
                for (let j = i + 1; j < prog.length; j++) {
                    if ((prog[i] === prog[j]) === vrai) paires.push([i, j]);
                }
            }
            if (!paires.length) return null;
            const [i, j] = randomChoice(paires);
            return { l: `Les instructions n°${i + 1} et n°${j + 1} sont identiques.`, v: vrai, pos: [i + 1, j + 1] };
        },
    },
    {
        k: 'nb_type',
        b: (prog, vrai) => {
            const f = randomChoice(prog);
            const reel = prog.filter(x => x === f).length;
            const n = vrai ? reel : reel + randomChoice([-1, 1]);
            if (n < 0) return null;
            return { l: `L'instruction « ${f} » apparaît exactement ${n} fois dans ce programme.`, v: vrai, pos: [] };
        },
    },
    {
        k: 'premiere',
        b: (prog, vrai) => {
            const dir = vrai ? prog[0] : randomChoice(FLECHES.filter(f => f !== prog[0]));
            return { l: `La première instruction fait ${DIRS[dir].nom}.`, v: vrai, pos: [1] };
        },
    },
    {
        k: 'derniere',
        b: (prog, vrai) => {
            const fin = prog[prog.length - 1];
            const dir = vrai ? fin : randomChoice(FLECHES.filter(f => f !== fin));
            return { l: `La dernière instruction fait ${DIRS[dir].nom}.`, v: vrai, pos: [prog.length] };
        },
    },
];

const construireLectureProgrammeVf = () => {
    const longueur = randInt(5, 6);
    const prog = [];
    for (let i = 0; i < longueur; i++) prog.push(randomChoice(FLECHES));

    const affichage = prog.map((f, i) => `${i + 1}) ${f}`).join('&nbsp;&nbsp; ');
    const texte = `<p>Voici un programme, lu de gauche à droite :</p>`
        + `<p><strong>${affichage}</strong></p>`
        + `<p>Pour chaque affirmation sur ce programme, indique si elle est vraie ou fausse.</p>`;

    // 4 clés distinctes, positions visées deux à deux disjointes, quota variable.
    for (let essai = 0; essai < 60; essai++) {
        const nbV = randInt(1, 3);
        const camps = shuffle([...Array(4)].map((_, i) => i < nbV));
        const builders = shuffle([...BUILDERS_VF_687]).slice(0, 4);
        const items = builders.map((bd, i) => bd.b(prog, camps[i]));
        if (items.some(x => x === null)) continue;
        const positions = items.flatMap(x => x.pos);
        if (new Set(positions).size !== positions.length) continue;
        const lignes = items.map(x => x.l);
        if (new Set(lignes).size !== 4) continue;
        const bonnesReponses = items.map(x => (x.v ? 0 : 1));
        const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_VF));
        payload._meta.generatorKey = 'lecture_programme_vf';
        return payload;
    }
    return construireLectureProgrammeVf();
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    programme_vers_chemin_qcm: genProgrammeVersCheminQcm,
    chemin_vers_programme_qcm: genCheminVersProgrammeQcm,
    instruction_fautive_qcm:   genInstructionFautiveQcm,
    programmes_chemins_relier: genProgrammesCheminReliers,
    lecture_programme_vf:      genLectureProgrammeVf,
};

export const CATEGORIES = [
    {
        id: 'instructions_sequences',
        label: 'Identifier une instruction ou une séquence d\'instructions',
        formatsList: [
            { key: 'programme_vers_chemin_qcm', label: L_PVC },
            { key: 'chemin_vers_programme_qcm', label: L_CVP },
            { key: 'instruction_fautive_qcm',   label: L_FAUT },
            { key: 'programmes_chemins_relier', label: L_REL },
            { key: 'lecture_programme_vf',      label: L_VF },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    TAILLE,
    DIRS,
    OPPOSE,
    executer,
    tirerChemin,
    tirerLeurres,
    cleChemin,
    BUILDERS_VF_687,
    genProgrammeVersCheminQcm,
    genCheminVersProgrammeQcm,
    genInstructionFautiveQcm,
    genProgrammesCheminReliers,
    genLectureProgrammeVf,
};
